import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGetStarted = async (tier, packageIdOverride) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    if (tier === 'free') {
      navigate('/dashboard');
      return;
    }

    if (tier === 'pro' || packageIdOverride) {
      setLoading(true);
      setError('');
      
      try {
        const token = localStorage.getItem('token');
        const originUrl = window.location.origin;
        
        const response = await axios.post(
          `${BACKEND_URL}/api/payments/checkout`,
          {
            package_id: packageIdOverride || 'pro_monthly',
            origin_url: originUrl
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Redirect to Stripe checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } catch (err) {
        console.error('Payment error:', err);
        setError(err.response?.data?.detail || 'Failed to initiate payment. Please try again.');
        setLoading(false);
      }
    }
    
    if (tier === 'enterprise') {
      alert('Enterprise tier coming soon! Contact us for more information.');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Seo
        title="Pricing & Credits – GameOS"
        description="GameOS pricing: start free with 100 credits, upgrade to Pro for 2,000 monthly credits, or top up credit packs. Keep 90% of all ad revenue your games earn."
        path="/pricing"
      />
      <Navbar />

      {/* Pricing Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">Pricing</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Get started immediately for free. Upgrade for more credits, usage and collaboration.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Free Tier */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
            <div className="mb-6">
              <span className="text-5xl font-bold text-white">$0</span>
              <span className="text-gray-400">/month</span>
            </div>
            <Button
              onClick={() => handleGetStarted('free')}
              className="w-full bg-white text-black hover:bg-gray-200 mb-6"
            >
              Start Building
            </Button>
            <div className="space-y-4">
              <p className="text-gray-400 font-semibold">Get started with:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">Create 1 game for free</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">100 credits for game editing</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">Basic ad integration</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">90% revenue share</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pro Tier */}
          <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-2xl p-8 border-2 border-purple-500 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                MOST POPULAR
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
            <div className="mb-6">
              <span className="text-5xl font-bold text-white">$19.99</span>
              <span className="text-gray-300">/month</span>
            </div>
            <Button
              onClick={() => handleGetStarted('pro')}
              disabled={loading}
              className="w-full bg-white text-purple-900 hover:bg-gray-200 mb-6 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Upgrade'}
            </Button>
            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-2 rounded-lg text-sm mb-4">
                {error}
              </div>
            )}
            <div className="bg-black/20 rounded-lg p-3 mb-6">
              <p className="text-white font-semibold">2000 credits / month</p>
            </div>
            <div className="space-y-4">
              <p className="text-purple-200 font-semibold">Everything in Free, plus:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">2000 credits / month</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white flex items-center gap-2">
                    1500 extra credits first month
                    <span className="bg-pink-600 text-white text-xs px-2 py-0.5 rounded-full">LIMITED TIME</span>
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">AI image generation</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Action / VFX animation generation</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Tileset generation</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Sound effect generation</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Background music generation</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Custom domains</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Remove the GameOS badge</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Export the project code</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-white">Priority support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enterprise Tier */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
            <div className="mb-6">
              <span className="text-5xl font-bold text-white">Custom</span>
            </div>
            <Button
              onClick={() => handleGetStarted('enterprise')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white mb-6"
            >
              Coming Soon
            </Button>
            <div className="space-y-4">
              <p className="text-gray-400 font-semibold">Everything in Pro, plus:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">Dedicated support</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">Custom integrations</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">SSO</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">White-label solution</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">SLA guarantee</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Credit Packs */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-4 text-center">Need more credits?</h2>
          <p className="text-gray-400 text-center mb-6">
            Buy additional credits any time. $25 per 1000 credits, with a 5% discount for each extra 1000.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 text-center">
              <h3 className="text-lg font-semibold text-white mb-2">1,000 Credits</h3>
              <div className="text-3xl font-bold text-white mb-1">$25</div>
              <p className="text-xs text-gray-400 mb-4">$25 / 1000 credits</p>
              <Button
                className="w-full bg-white text-black hover:bg-gray-200 text-sm"
                onClick={() => handleGetStarted('pro', 'credits_1000')}
              >
                Buy 1,000 Credits
              </Button>
            </div>

            <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-2xl p-6 border border-purple-500/60 text-center">
              <h3 className="text-lg font-semibold text-white mb-2">2,000 Credits</h3>
              <div className="text-3xl font-bold text-white mb-1">$47.50</div>
              <p className="text-xs text-purple-200 mb-4">5% discount vs 2×1,000</p>
              <Button
                className="w-full bg-white text-purple-900 hover:bg-gray-200 text-sm font-semibold"
                onClick={() => handleGetStarted('pro', 'credits_2000')}
              >
                Buy 2,000 Credits
              </Button>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 text-center">
              <h3 className="text-lg font-semibold text-white mb-2">3,000 Credits</h3>
              <div className="text-3xl font-bold text-white mb-1">$67.50</div>
              <p className="text-xs text-gray-400 mb-4">10% discount vs 3×1,000</p>
              <Button
                className="w-full bg-white text-black hover:bg-gray-200 text-sm"
                onClick={() => handleGetStarted('pro', 'credits_3000')}
              >
                Buy 3,000 Credits
              </Button>
            </div>
          </div>
        </div>

        {/* How credits work */}
        <div className="mt-16 max-w-4xl mx-auto" data-testid="pricing-credits-explainer">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">How Credits Work</h2>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-white/10 space-y-4 text-gray-300">
            <p>
              Credits are the fuel for AI generation on GameOS. Starting a brand new game with the
              conversational agent costs 10 credits, and each follow-up refinement — changing mechanics,
              adding levels, restyling visuals — costs just 2 credits. Generating individual assets is even
              cheaper: sprites cost 3 credits, while tilesets and backgrounds cost 4 credits each.
            </p>
            <p>
              You are only ever charged for successful builds. If a generation fails, gets cut short, or you
              cancel it mid-build, no credits are deducted. Visual tweaks in the Tilemap and Difficulty
              editors are always free, and remixing another creator's public game into your own session
              costs nothing to start.
            </p>
            <p>
              Every free account begins with 100 credits — enough to build a full game and iterate on it
              several times. Pro subscribers receive 2,000 fresh credits every month plus priority access to
              flagship models like GPT-6 Astra and Claude Fable 5.1, and one-time credit packs never expire,
              so you can top up whenever a big project calls for it.
            </p>
          </div>
        </div>

        {/* Pricing FAQ */}
        <div className="mt-16 max-w-4xl mx-auto" data-testid="pricing-faq">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Pricing Questions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">Can I cancel my Pro plan anytime?</h3>
              <p className="text-gray-400">Yes. Your subscription is managed through Stripe and you can cancel whenever you like — you keep any remaining credits for the current billing period.</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">Do credit packs expire?</h3>
              <p className="text-gray-400">No. One-time credit packs stay on your account until you use them, whether that's this week or next year.</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">Which AI models are included?</h3>
              <p className="text-gray-400">All 55 models on the roster — including GPT-6 Astra, Claude Fable 5.1, Claude Opus 5 and Gemini 3.8 Flash — are available on every plan. Credit costs are the same regardless of model.</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">How do I get paid?</h3>
              <p className="text-gray-400">Enable ads on any game and revenue accrues per impression. You keep 90% and can claim your earnings from the dashboard at any time.</p>
            </div>
          </div>
        </div>

        {/* Revenue Share Info */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-2xl p-8 border border-green-500/30">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Fair Revenue Sharing</h3>
            <p className="text-gray-300 text-center mb-6">
              Unlike other platforms, we believe in fair compensation for creators.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="text-6xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  90%
                </div>
                <div className="text-xl text-white font-semibold mb-2">You Keep</div>
                <p className="text-gray-400">All ad revenue from your games goes directly to you</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold text-gray-400 mb-2">10%</div>
                <div className="text-xl text-white font-semibold mb-2">Platform Fee</div>
                <p className="text-gray-400">Fair platform fee to keep GameOS running and improving</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;